import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const seedV = {
    hidden: { scale: 0, y: -14, opacity: 0 },
    show: { scale: 1, y: 0, opacity: 1, transition: { type: "spring", stiffness: 220, damping: 14 } },
};
const stemV = {
    hidden: { scaleY: 0 },
    show: { scaleY: 1, transition: { duration: 0.45, delay: 0.5, ease: "easeOut" } },
};
const leafV = {
    hidden: { scale: 0, opacity: 0 },
    show: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 200, damping: 12 } },
};
const petalV = {
    hidden: { scale: 0, opacity: 0 },
    show: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 180, damping: 11 } },
};

export default function SeedToBloomIntro() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!reduce && !sessionStorage.getItem("lsf_intro_seen")) setShow(true);
    }, []);

    const close = () => {
        sessionStorage.setItem("lsf_intro_seen", "1");
        setShow(false);
    };

    useEffect(() => {
        if (!show) return undefined;
        const t = setTimeout(close, 2350);
        return () => clearTimeout(t);
    }, [show]);

    if (!show) return null;

    return (
        <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.4 } }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-cream flex flex-col items-center justify-center cursor-pointer"
            data-testid="seed-intro-overlay"
            role="button"
            aria-label="Intro animation — tap to skip"
        >
            <motion.svg
                width="120"
                height="140"
                viewBox="0 0 120 140"
                variants={container}
                initial="hidden"
                animate="show"
                aria-hidden="true"
            >
                {/* seed */}
                <motion.g variants={seedV} style={{ transformOrigin: "60px 96px" }}>
                    <ellipse cx="60" cy="96" rx="11" ry="14" fill="#A9805B" />
                    <path d="M55 86c2-3 8-3 10 0" stroke="#3D3229" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" />
                </motion.g>
                {/* soil mound */}
                <motion.path variants={seedV} d="M34 106c8-6 44-6 52 0-8 4-44 4-52 0z" fill="#E07A5F" />
                {/* stem */}
                <motion.g variants={stemV} style={{ transformOrigin: "60px 96px" }}>
                    <path d="M60 96V58" stroke="#5E8355" strokeWidth="5" strokeLinecap="round" fill="none" />
                </motion.g>
                {/* leaves */}
                <motion.g variants={leafV} style={{ transformOrigin: "60px 84px" }} transition={{ delay: 0.85 }}>
                    <path d="M60 84c-16 2-22-4-24-14 12-2 21 3 24 14z" fill="#8FB38A" />
                </motion.g>
                <motion.g variants={leafV} style={{ transformOrigin: "60px 76px" }} transition={{ delay: 1 }}>
                    <path d="M60 76c12-8 24-8 30-2-6 8-19 9-30 2z" fill="#8FB38A" />
                </motion.g>
                {/* flower petals */}
                {[
                    [60, 36, 0],
                    [76, 48, 0],
                    [70, 66, 0],
                    [50, 66, 0],
                    [44, 48, 0],
                ].map(([cx, cy], i) => (
                    <motion.g key={i} variants={petalV} style={{ transformOrigin: `${cx}px ${cy}px` }} transition={{ delay: 1.25 + i * 0.07 }}>
                        <circle cx={cx} cy={cy} r="11" fill="#E07A5F" opacity="0.95" />
                    </motion.g>
                ))}
                <motion.g variants={petalV} style={{ transformOrigin: "60px 52px" }} transition={{ delay: 1.6 }}>
                    <circle cx="60" cy="52" r="9.5" fill="#F6C544" />
                </motion.g>
            </motion.svg>

            <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.7, duration: 0.4 }}
                className="mt-6 font-display font-semibold text-xl text-soil"
            >
                Little Seeds Flourish
            </motion.p>

            <span className="absolute bottom-8 text-xs font-bold tracking-widest uppercase text-soil/40" data-testid="intro-skip-button">
                Tap to skip
            </span>
        </motion.div>
    );
}
